import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ElementType,
} from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  Switch,
  Card,
  CardBody,
  Spinner,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalContent,
  Textarea,
  useDisclosure,
  Divider,
  Badge,
  useToast,
  Avatar,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { FacebookComment, FacebookProfile } from '../../../utils/types';
import { useParams } from 'react-router-dom';
import {
  getFacebookComments,
  replyToFacebookComment,
  toggleFacebookAiCommentResponses,
} from '../../../hooks/facebook_comments';
import { getFacebookProfile } from '../../../hooks/facebook';
import { FaFacebook } from 'react-icons/fa';
import { format, isToday, isYesterday, differenceInDays } from 'date-fns';

const FacebookCommentsPage = () => {
  const { orgId, facebookAccId } = useParams();
  const [comments, setComments] = useState<FacebookComment[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [accountData, setAccountData] = useState<FacebookProfile>();
  const [loading, setLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState<boolean>(false);
  const isFirstLoad = useRef(true);
  const aiInitialized = useRef(false);

  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedComment, setSelectedComment] =
    useState<FacebookComment | null>(null);
  const [replyText, setReplyText] = useState('');

  const loaderRef = useRef<HTMLDivElement>(null);

  function formatConversationTime(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ''; // invalid date safety

    // Today → show time
    if (isToday(date)) {
      return format(date, 'hh:mm a');
    }

    // Yesterday
    if (isYesterday(date)) {
      return 'Yesterday';
    }

    // Within the last 7 days → day of week
    const daysDiff = differenceInDays(new Date(), date);
    if (daysDiff < 7) {
      return format(date, 'EEEE'); // Monday, Tuesday...
    }

    // Older → format date
    // return format(date, 'dd MMM yyyy'); // 28 Nov 2025
    return format(date, 'dd MMM'); // 28 Nov 2025
  }

  const fetchComments = async (url: any = null) => {
    if (loading) return;
    setLoading(true);

    const response = await getFacebookComments(
      orgId,
      facebookAccId,
      url || nextUrl,
    );

    if (response.success) {
      const data = response.data;
      console.log('Fetched comments:', data);
      setComments((prev) => [...prev, ...(Array.isArray(data) ? data : [])]);
      // setComments((prev) => [...prev, ...data?.results]);
      setNextUrl(data?.next);
    }

    setLoading(false);
  };

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        setLoading(true);
        let response = await getFacebookProfile(orgId);
        console.log('getFacebookProfile', response);
        setAccountData(response.data[0]);
        setAiEnabled(response.data[0].ai_comment_responses);
        console.log(
          'AI Comment Responses Enabled:',
          response.data[0].ai_comment_responses,
        );
        aiInitialized.current = true;
      } catch (error) {
        toast({
          title: 'Error fetching account',
          description: 'An error occured.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAccount();
  }, [orgId]);

  // Load first page
  useEffect(() => {
    fetchComments();
  }, []);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && nextUrl) {
        fetchComments(nextUrl);
      }
    },
    [nextUrl],
  );

  useEffect(() => {
    const element = loaderRef.current;
    const option = { root: null, rootMargin: '20px', threshold: 0 };

    const observer = new IntersectionObserver(handleObserver, option);
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  useEffect(() => {
    // Wait until we have the real value from backend
    if (!aiInitialized.current) return;

    toggleAiCommentResponsesCall(aiEnabled);
  }, [aiEnabled]);

  const openReplyModal = (comment: FacebookComment) => {
    setSelectedComment(comment);
    onOpen();
  };

  const sendReply = async () => {
    if (!selectedComment) return;

    const response = await replyToFacebookComment(
      orgId || '',
      facebookAccId || '',
      selectedComment?.id,
      replyText,
    );

    if (response.success) {
      setReplyText('');
      onClose();
    }
  };

  const toggleAiCommentResponsesCall = async (value: boolean) => {
    const response = await toggleFacebookAiCommentResponses(
      orgId || '',
      facebookAccId || '',
      value,
    );

    console.log('AI Auto-Response toggle response:', response);

    if (!response.success) {
      toast({ title: 'Failed to update', status: 'error' });
    }
  };

  return (
    <Box maxW="100%" mx="auto">
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        bg={'white'}
        position={'sticky'}
        top={0}
        zIndex={10}
        w={'100%'}
        left={0}
        right={0}
        py={4}
        px={6}
      >
        <HStack
          maxW="800px"
          alignSelf={'center'}
          justifyContent={'space-between'}
          w={'100%'}
          margin={'0 auto'}
        >
          <Icon as={FaFacebook as ElementType} boxSize={8} color="#1877F2" />
          <Text fontSize="2xl" fontWeight="bold">
            Facebook Comments
          </Text>
          <Flex align="center" gap={2}>
            <Text>AI Auto-Response</Text>
            <Switch
              isChecked={aiEnabled}
              onChange={(e) => setAiEnabled(e.target.checked)}
              size="sm"
              colorScheme="blue"
            />
          </Flex>
        </HStack>
      </Flex>

      <Divider mb={6} />

      {/* COMMENT LIST */}
      <Box px={6} pb={6} maxW="800px" mx="auto">
        {comments.map((c) => (
          <Card
            key={c.id}
            mb={4}
            borderRadius="lg"
            shadow="sm"
            border="1px solid"
            borderColor="gray.100"
            bg="white"
            _hover={{
              shadow: 'md',
              borderColor: 'blue.300',
              cursor: 'pointer',
            }}
            onClick={() => openReplyModal(c)}
          >
            <CardBody>
              {/* Parent comment */}
              {c.parent_comment && (
                <Box
                  bg="gray.50"
                  p={3}
                  borderRadius="md"
                  mb={3}
                  fontSize="sm"
                  color="gray.600"
                  borderLeft="3px solid"
                  borderColor="facebook.300"
                >
                  <Text noOfLines={1}>
                    Replying to <b>{c.parent_comment.sender_name}</b>:{' '}
                    {c.parent_comment.text}
                  </Text>
                </Box>
              )}

              <Flex justify="space-between" align="center">
                <Flex gap={3} align="center">
                  <Avatar
                    size="sm"
                    name={c.sender_name || 'user'}
                    color="white"
                    bg="#1877F2"
                  />
                  <Text>{c.sender_name}</Text>
                </Flex>

                <Badge colorScheme="facebook" variant="subtle">
                  {formatConversationTime(c.created_time || '')}
                </Badge>
              </Flex>

              <Text mt={3} fontSize="md">
                {c.text}
              </Text>

              {c.replies && c.replies?.length > 0 && (
                <Text
                  mt={3}
                  fontSize="sm"
                  color="facebook.500"
                  fontWeight="500"
                >
                  {c.replies.length} replies →
                </Text>
              )}
            </CardBody>
          </Card>
        ))}

        {/* Infinite Scroll Loader */}
        {loading && (
          <Flex justify="center" my={6}>
            <Spinner size="lg" color="blue" />
          </Flex>
        )}

        <div ref={loaderRef} />
      </Box>

      {/* REPLY MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Respond to Comment</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {selectedComment && (
              <>
                {selectedComment.parent_comment && (
                  <Box
                    bg="gray.100"
                    p={3}
                    borderRadius="md"
                    mb={4}
                    fontSize="sm"
                    color="gray.600"
                  >
                    Replying to{' '}
                    <b>{selectedComment.parent_comment.sender_name}</b>:
                    <br />
                    {selectedComment.parent_comment.text}
                    <Badge colorScheme="facebook" variant="subtle">
                      {formatConversationTime(
                        selectedComment.parent_comment.created_time || '',
                      )}
                    </Badge>
                  </Box>
                )}

                <Text fontWeight="bold">{selectedComment.sender_name}</Text>
                <Badge colorScheme="blue" variant="subtle">
                  {formatConversationTime(selectedComment.created_time || '')}
                </Badge>
                <Text mb={4}>{selectedComment.text}</Text>

                <Textarea
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              color={'white'}
              bg={'#1877F2'}
              mr={3}
              onClick={sendReply}
              rightIcon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M3.4 20.6L22 12 3.4 3.4 3 10l10 2-10 2z" />
                </svg>
              }
            >
              Send Reply
            </Button>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FacebookCommentsPage;
